import { supabase } from "@/integrations/supabase/client";

export type StoredTheme = "system" | "light" | "dark";

export type ProfilePrefs = {
  theme: StoredTheme;
  toursSeen: string[];
};

/** Read account-level UI preferences. Returns null when signed out / unavailable. */
export async function fetchProfilePrefs(): Promise<ProfilePrefs | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData.session?.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("theme_preference, tours_seen")
    .eq("id", uid)
    .maybeSingle();
  if (error || !data) return null;
  const t = data.theme_preference;
  return {
    theme: t === "light" || t === "dark" || t === "system" ? t : "dark",
    toursSeen: Array.isArray(data.tours_seen) ? data.tours_seen : [],
  };
}

export async function persistThemePreference(theme: StoredTheme): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData.session?.user?.id;
  if (!uid) return;
  await supabase.from("profiles").update({ theme_preference: theme }).eq("id", uid);
}

/** Append a tour key to the account's completed list (idempotent). */
export async function markTourSeen(key: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const uid = sessionData.session?.user?.id;
  if (!uid) return;
  const { data } = await supabase.from("profiles").select("tours_seen").eq("id", uid).maybeSingle();
  const current: string[] = Array.isArray(data?.tours_seen) ? data!.tours_seen : [];
  if (current.includes(key)) return;
  await supabase.from("profiles").update({ tours_seen: [...current, key] }).eq("id", uid);
}
